import React, { useState } from 'react';
import { IAuditoriaDepreciacaoResultado, IItemDepreciacao, IFundamentacaoLink, IContaDetalheDepreciacao } from '../interfaces/IAuditoriaDepreciacao';
import { formatCurrency, formatDate } from '../utils/formatters';

// COR-275: drill-down a nível de lançamento — todo ERRO/ALERTA/AVISO precisa mostrar não só a
// conta/valor agregado, mas os lançamentos reais que causaram aquele número.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemDepreciacao }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div style={estilos.lancamentosBox}>
      <div style={estilos.secaoLabel}>
        <i className='bi bi-list-columns-reverse me-1' />
        Lançamentos Relacionados ({lancamentos.length})
      </div>
      <div className='table-responsive'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code style={estilos.code}>{lanc.numeroLancamento || '-'}</code></td>
                <td><code style={estilos.code}>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

// COR-178: nome da conta contábil correspondente ao código, quando disponível — usado para
// exibir "69 - (-) Depreciações de Móveis e Utensílios" em vez de só o código "69".
const descricaoDaConta = (codigo: string, detalhes: IContaDetalheDepreciacao[]): string =>
  detalhes.find((d) => d.codigo === codigo)?.descricao?.trim() || '';

interface Props {
  resultado: IAuditoriaDepreciacaoResultado;
}


const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':   return 'bi-exclamation-circle-fill';
    case 'ALERTA': return 'bi-exclamation-triangle-fill';
    case 'AVISO':  return 'bi-info-circle-fill';
    default:       return 'bi-check-circle-fill';
  }
};

// COR-177: resumo curto (sem URL) + botões "Ver X" para as referências que têm link — substitui
// o texto corrido com URLs completas que antes era impresso direto no diagnóstico.
const BlocoFundamentacao: React.FC<{ titulo: string; resumo: string; links: IFundamentacaoLink[] }> = ({ titulo, resumo, links }) => (
  <div style={estilos.fundamentacao}>
    <strong>{titulo}:</strong> {resumo}
    {links.length > 0 && (
      <span style={{ marginLeft: 8 }}>
        {links.map((link) => (
          <a
            key={link.url}
            href={link.url}
            target='_blank'
            rel='noopener noreferrer'
            className='btn btn-sm btn-light-primary'
            style={{ marginRight: 6, padding: '2px 8px', fontSize: 11 }}
          >
            {link.rotulo}
          </a>
        ))}
      </span>
    )}
  </div>
);

const splitContas = (contas: string): string[] =>
  contas ? contas.split(',').map((c) => c.trim()).filter(Boolean) : [];

// ─── Linha resumida: célula "Contas de Ativo" compacta ──────────────────────

const CelulaContaAtivo: React.FC<{ item: IItemDepreciacao }> = ({ item }) => {
  const contas = splitContas(item.contaAtivo);
  if (contas.length === 0) return <span className='text-muted'>-</span>;
  if (contas.length === 1) {
    const descricao = descricaoDaConta(contas[0], item.contasAtivoDetalhe);
    return (
      <span title={descricao || undefined}>
        <code style={estilos.code}>{contas[0]}</code>
        {descricao && <span style={{ ...estilos.contaDescricao, marginLeft: 6 }}>{descricao}</span>}
      </span>
    );
  }
  // Tooltip com "código - nome" de cada conta, não só os códigos crus.
  const tituloDetalhado = contas
    .map((conta) => {
      const descricao = descricaoDaConta(conta, item.contasAtivoDetalhe);
      return descricao ? `${conta} - ${descricao}` : conta;
    })
    .join(', ');
  return (
    <span>
      <span style={estilos.contasBadge} title={tituloDetalhado}>
        <i className='bi bi-collection me-1' style={{ fontSize: 11 }} />
        {contas.length} contas
      </span>
    </span>
  );
};

// ─── Linha resumida: célula "Conta Redutora" compacta (coluna da tabela de anomalias) ────────

const CelulaContaRedutora: React.FC<{ item: IItemDepreciacao }> = ({ item }) => {
  const contas = splitContas(item.contaRedutora);
  if (contas.length === 0) return <span className='text-muted' style={{ fontSize: 11 }}>nenhuma</span>;
  if (contas.length === 1) {
    const descricao = descricaoDaConta(contas[0], item.contasRedutoraDetalhe);
    return (
      <span title={descricao || undefined}>
        <code style={estilos.code}>{contas[0]}</code>
        {descricao && <span style={{ ...estilos.contaDescricao, marginLeft: 6 }}>{descricao}</span>}
      </span>
    );
  }
  const tituloDetalhado = contas
    .map((conta) => {
      const descricao = descricaoDaConta(conta, item.contasRedutoraDetalhe);
      return descricao ? `${conta} - ${descricao}` : conta;
    })
    .join(', ');
  return (
    <span style={estilos.contasBadge} title={tituloDetalhado}>
      {contas.length} contas
    </span>
  );
};

// ─── Painel expandido: contas detalhadas + diagnóstico ───────────────────────

const LIMITE_CONTAS_VISIVEIS = 8;

const PainelDetalhes: React.FC<{ item: IItemDepreciacao }> = ({ item }) => {
  const [mostrarTodasAtivo, setMostrarTodasAtivo] = useState(false);
  const contasAtivo    = splitContas(item.contaAtivo);
  const contasRedutora = splitContas(item.contaRedutora);
  const ativoVisiveis  = mostrarTodasAtivo
    ? contasAtivo
    : contasAtivo.slice(0, LIMITE_CONTAS_VISIVEIS);

  return (
    <div style={estilos.painel}>

      {/* Bloco de contas */}
      <div style={estilos.gridContas}>

        {/* Contas de Ativo */}
        <div>
          <div style={estilos.secaoLabel}>
            <i className='bi bi-layers me-1' />
            Contas de Ativo ({contasAtivo.length})
          </div>
          {contasAtivo.length === 0 ? (
            <span className='text-muted'>-</span>
          ) : (
            <>
              {ativoVisiveis.map((conta, idx) => (
                <div key={conta} style={estilos.linhaContas}>
                  <code style={estilos.code}>{conta}</code>
                  {descricaoDaConta(conta, item.contasAtivoDetalhe) && (
                    <span style={estilos.contaDescricao}>{descricaoDaConta(conta, item.contasAtivoDetalhe)}</span>
                  )}
                  {idx === 0
                    ? <span style={estilos.badgePrincipal}>Principal</span>
                    : <span style={estilos.badgeAuxiliar}>Auxiliar</span>
                  }
                </div>
              ))}
              {contasAtivo.length > LIMITE_CONTAS_VISIVEIS && !mostrarTodasAtivo && (
                <button
                  type='button'
                  className='btn btn-link btn-sm p-0 mt-1'
                  style={{ fontSize: 12 }}
                  onClick={() => setMostrarTodasAtivo(true)}
                >
                  + {contasAtivo.length - LIMITE_CONTAS_VISIVEIS} conta(s) adicionais
                </button>
              )}
            </>
          )}
        </div>

        {/* Conta Redutora */}
        <div>
          <div style={estilos.secaoLabel}>
            <i className='bi bi-dash-circle me-1' />
            Conta Redutora ({contasRedutora.length || 'nenhuma'})
          </div>
          {contasRedutora.length === 0 ? (
            <span className='text-muted' style={{ fontSize: 12, fontStyle: 'italic' }}>
              Nenhuma conta redutora vinculada nesta categoria
            </span>
          ) : (
            contasRedutora.map((conta) => (
              <div key={conta} style={estilos.linhaContas}>
                <code style={estilos.codeRedutora}>{conta}</code>
                {descricaoDaConta(conta, item.contasRedutoraDetalhe) && (
                  <span style={estilos.contaDescricao}>{descricaoDaConta(conta, item.contasRedutoraDetalhe)}</span>
                )}
                <span style={estilos.badgeRedutora}>Redutora</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Valores-chave */}
      <div style={estilos.valoresGrid}>
        <div style={estilos.valorBloco}>
          <div style={estilos.valorLabel}>Saldo Total do Ativo</div>
          <div style={estilos.valorNumero}>{formatCurrency(item.valorAtivo)}</div>
        </div>
        <div style={estilos.valorBloco}>
          <div style={estilos.valorLabel}>Movimento da Redutora</div>
          <div style={estilos.valorNumero}>{formatCurrency(item.valorMovimento)}</div>
        </div>
        {item.exposicaoContabil > 0 && (
          <div style={estilos.valorBloco}>
            <div style={estilos.valorLabel}>Exposição Contábil</div>
            <div style={{ ...estilos.valorNumero, color: '#9b1c1c' }}>{formatCurrency(item.exposicaoContabil)}</div>
          </div>
        )}
        <div style={estilos.valorBloco}>
          <div style={estilos.valorLabel}>Competência</div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{item.competencia}</div>
        </div>
        <div style={estilos.valorBloco}>
          <div style={estilos.valorLabel}>Natureza</div>
          <div><span className='auditoria-natureza-badge'>{item.natureza}</span></div>
        </div>
      </div>

      {/* Diagnóstico */}
      <div style={estilos.diagnosticoBox}>
        <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
      </div>

      {/* COR-209: movimentação do Ativo vinculada ou potencialmente relacionada à variação —
          bloco estruturado próprio (não só embutida no texto do diagnóstico), com data, valor e
          histórico, para dar rastreabilidade entre a baixa/aquisição do bem e a movimentação da
          conta redutora, mesmo quando ocorrida em competência diferente da variação. */}
      {item.movimentacaoAtivoRelacionada && (
        <div style={estilos.movimentoAtivoBox}>
          <div style={estilos.secaoLabel}>
            <i className='bi bi-link-45deg me-1' />
            Movimentação do Ativo Vinculada/Relacionada
          </div>
          <div style={{ fontSize: 13 }}>
            <span className='auditoria-natureza-badge'>{item.movimentacaoAtivoRelacionada.tipo}</span>{' '}
            em <strong>{formatDate(item.movimentacaoAtivoRelacionada.data)}</strong>
            {' '}(competência {item.movimentacaoAtivoRelacionada.competencia}, variação em {item.movimentacaoAtivoRelacionada.competenciaVariacao})
            {' — '}
            <strong>{formatCurrency(item.movimentacaoAtivoRelacionada.valor)}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}>
            <strong>Histórico:</strong> {item.movimentacaoAtivoRelacionada.historico}
          </div>
        </div>
      )}

      {/* COR-210: categoria zerada durante o período — saldo final R$ 0,00 não é sinônimo de
          "sem movimento"/CONCILIADO quando havia saldo inicial ou movimentação real que reduziu
          o saldo a zero (reclassificação, estorno ou baixa). Bloco dedicado, separado do
          diagnóstico, para deixar claro o que aconteceu e exigir justificativa. */}
      {item.zeradaNoPeriodo && item.detalheZeramento && (
        <div style={estilos.zeramentoBox}>
          <div style={estilos.secaoLabel}>
            <i className='bi bi-exclamation-triangle-fill me-1' />
            Categoria Zerada no Período — Pendente de Justificativa
          </div>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            {item.eventosZeramentoIdentificados.map((evento) => (
              <span key={evento} className='auditoria-natureza-badge' style={{ marginRight: 6 }}>{evento}</span>
            ))}
          </div>
          <div style={estilos.valoresGrid}>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Saldo Inicial (Ativo)</div>
              <div style={estilos.valorNumero}>{formatCurrency(item.detalheZeramento.saldoInicialAtivo)}</div>
            </div>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Entradas (débitos) no Período</div>
              <div style={estilos.valorNumero}>{formatCurrency(item.detalheZeramento.totalEntradasAtivo)}</div>
            </div>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Baixas (créditos) no Período</div>
              <div style={estilos.valorNumero}>{formatCurrency(item.detalheZeramento.totalBaixasAtivo)}</div>
            </div>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Saldo Final (Ativo)</div>
              <div style={estilos.valorNumero}>{formatCurrency(0)}</div>
            </div>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Saldo Inicial (Redutora)</div>
              <div style={estilos.valorNumero}>{formatCurrency(item.detalheZeramento.saldoInicialRedutora)}</div>
            </div>
            <div style={estilos.valorBloco}>
              <div style={estilos.valorLabel}>Movimento Redutora no Período</div>
              <div style={estilos.valorNumero}>{formatCurrency(item.detalheZeramento.totalMovimentoRedutora)}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>
            É obrigatório registrar na capa da conciliação a motivação da baixa, reclassificação ou estorno que zerou esta categoria.
          </div>
        </div>
      )}

      {/* COR-275: lançamentos individuais que originaram o ERRO/ALERTA/AVISO desta categoria —
          drill-down até a linha da ECD, não só o saldo/valor agregado da conta. */}
      <BlocoLancamentosRelacionados item={item} />

      {/* Memória de Cálculo */}
      <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
        <strong>Memória de Cálculo:</strong> {item.memoriaCalculo}
      </div>

      {/* Fundamentação */}
      <BlocoFundamentacao titulo='Fundamentação Contábil' resumo={item.fundamentacaoContabilResumo} links={item.fundamentacaoContabilLinks} />
      <div style={{ marginTop: 4 }}>
        <BlocoFundamentacao titulo='Fundamentação Tributária' resumo={item.fundamentacaoTributariaResumo} links={item.fundamentacaoTributariaLinks} />
      </div>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────

const AuditoriaDepreciacaoDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido]     = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const itens        = resultado.itens || [];
  const itensCriticos = itens.filter((i) => i.status === 'ERRO' || i.status === 'ALERTA');

  const alternarExpandir = (chave: string) =>
    setItemExpandido(itemExpandido === chave ? null : chave);

  const chaveItem = (item: IItemDepreciacao) =>
    `${item.empresaId}-${item.categoria}-${item.contaRedutora}`;

  return (
    <>
      {/* Banner de status */}
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Paridade em Depreciação, Amortização e Exaustão — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      {/* KPIs */}
      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.exposicaoContabilTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Exposição Contábil Estimada</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.exposicaoContabilTotal)}</div>
          {resultado.exposicaoContabilTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Sem Exposição Confirmada</span>
          ) : (
            <div className='auditoria-kpi-help'>
              Multa ECD potencial (5% limitada a 1% da receita bruta do período) aplicada somente a ERROs de natureza invertida. Divergências de paridade (ALERTA/AVISO) requerem análise manual.
            </div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Diagnóstico por Categoria</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'
              title='Categorias com inconsistência confirmada (natureza invertida ou lançamento indevido)'>
              {resultado.totalErros} ERRO{resultado.totalErros !== 1 ? 'S' : ''}
            </span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'
              title='Categorias com risco relevante, mas não conclusivo'>
              {resultado.totalAlertas} ALERTA{resultado.totalAlertas !== 1 ? 'S' : ''}
            </span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'
              title='Variações ou pontos de atenção sem erro confirmado'>
              {resultado.totalAvisos} AVISO{resultado.totalAvisos !== 1 ? 'S' : ''}
            </span>
          </div>
          <div className='auditoria-kpi-help'>
            Contagem <strong>por categoria</strong> — cada grupo de contas conta como 1.
            {resultado.totalOcorrencias > 0 && (
              <span style={{ display: 'block', marginTop: 4 }}>
                Total de contas de Ativo envolvidas: <strong>{resultado.totalOcorrencias}</strong>
              </span>
            )}
          </div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base contábil (CPC 27/CPC 04) e tributária (IN RFB 2.003/2021).</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <BlocoFundamentacao titulo='Fundamentação Contábil' resumo={resultado.fundamentacaoContabilResumo} links={resultado.fundamentacaoContabilLinks} />
          <div className='mt-2'>
            <BlocoFundamentacao titulo='Fundamentação Tributária' resumo={resultado.fundamentacaoTributariaResumo} links={resultado.fundamentacaoTributariaLinks} />
          </div>
        </div>
      )}

      {/* Anomalias críticas (ERRO / ALERTA) */}
      {itensCriticos.length > 0 && (
        <div className='auditoria-anomalias-depr'>
          <h4>
            <i className='bi bi-exclamation-circle-fill me-2' />
            Anomalias Detectadas ({itensCriticos.length} categoria{itensCriticos.length > 1 ? 's' : ''})
          </h4>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
            Clique em uma linha para ver contas detalhadas e diagnóstico completo.
          </p>
          <div className='table-responsive'>
            <table className='auditoria-table' style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Contas de Ativo</th>
                  <th>Conta Redutora</th>
                  <th className='text-end'>Saldo do Ativo</th>
                  <th className='text-end'>Mov. Redutora</th>
                  <th className='text-end'>Exposição Contábil</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {itensCriticos.map((item) => {
                  const chave    = `anomalia-${chaveItem(item)}`;
                  const expandido = itemExpandido === chave;
                  return (
                    <React.Fragment key={chave}>
                      <tr
                        style={{ background: item.status === 'ERRO' ? '#fff5f5' : '#fffbeb', cursor: 'pointer' }}
                        onClick={() => alternarExpandir(chave)}
                      >
                        <td><strong>{item.categoria}</strong></td>
                        <td><CelulaContaAtivo item={item} /></td>
                        <td><CelulaContaRedutora item={item} /></td>
                        <td className='text-end'>{formatCurrency(item.valorAtivo)}</td>
                        <td className='text-end'>{formatCurrency(item.valorMovimento)}</td>
                        <td className='text-end' style={{ color: '#9b1c1c', fontWeight: 800 }}>
                          {formatCurrency(item.exposicaoContabil)}
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0 }}>
                            <PainelDetalhes item={item} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Todas as categorias analisadas */}
      <div className='auditoria-card'>
        <h3>Categorias de Ativo Analisadas</h3>
        <p className='text-muted' style={{ fontSize: 13 }}>
          Clique em uma categoria para ver contas individuais, memória de cálculo e fundamentação.
        </p>

        {itens.length === 0 && (
          <div className='text-muted mt-4'>
            Nenhuma conta de Ativo/redutora de depreciação, amortização ou exaustão encontrada para os filtros selecionados.
          </div>
        )}

        {itens.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Natureza</th>
                  <th>Contas de Ativo</th>
                  <th>Conta Redutora</th>
                  <th className='text-end'>Saldo do Ativo</th>
                  <th className='text-end'>Movimento</th>
                  <th className='text-end'>Exposição Contábil</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => {
                  const chave    = `cat-${chaveItem(item)}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td><strong>{item.categoria}</strong></td>
                        <td><span className='auditoria-natureza-badge'>{item.natureza}</span></td>
                        <td><CelulaContaAtivo item={item} /></td>
                        <td><CelulaContaRedutora item={item} /></td>
                        <td className='text-end'>{formatCurrency(item.valorAtivo)}</td>
                        <td className='text-end'>{formatCurrency(item.valorMovimento)}</td>
                        <td className='text-end'>{formatCurrency(item.exposicaoContabil)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>
                      {expandido && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0 }}>
                            <PainelDetalhes item={item} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

// ─── Estilos inline centralizados ────────────────────────────────────────────

const estilos = {
  painel: {
    padding: '16px 20px',
    background: '#fafbfc',
    borderTop: '2px solid #e5e7eb',
  } as React.CSSProperties,

  gridContas: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    marginBottom: 16,
  } as React.CSSProperties,

  secaoLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: '#6b7280',
    letterSpacing: '0.06em',
    marginBottom: 8,
  } as React.CSSProperties,

  linhaContas: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 5,
  } as React.CSSProperties,

  code: {
    fontSize: 12,
    background: '#f3f4f6',
    padding: '2px 7px',
    borderRadius: 4,
    fontFamily: 'monospace',
  } as React.CSSProperties,

  codeRedutora: {
    fontSize: 12,
    background: '#fef3c7',
    padding: '2px 7px',
    borderRadius: 4,
    fontFamily: 'monospace',
  } as React.CSSProperties,

  // COR-178: nome da conta contábil ao lado do código (ex.: "(-) Depreciações de Móveis e
  // Utensílios"), para a tela não exibir só o código cru.
  contaDescricao: {
    fontSize: 12,
    color: '#374151',
  } as React.CSSProperties,

  badgePrincipal: {
    fontSize: 10,
    background: '#dbeafe',
    color: '#1d4ed8',
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 600,
  } as React.CSSProperties,

  badgeAuxiliar: {
    fontSize: 10,
    background: '#f3f4f6',
    color: '#6b7280',
    padding: '1px 6px',
    borderRadius: 4,
  } as React.CSSProperties,

  badgeRedutora: {
    fontSize: 10,
    background: '#fef3c7',
    color: '#92400e',
    padding: '1px 6px',
    borderRadius: 4,
    fontWeight: 600,
  } as React.CSSProperties,

  contasBadge: {
    fontSize: 11,
    background: '#eff6ff',
    color: '#1d4ed8',
    border: '1px solid #bfdbfe',
    padding: '2px 8px',
    borderRadius: 12,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'default',
  } as React.CSSProperties,

  valoresGrid: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 24,
    borderTop: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    paddingTop: 12,
    paddingBottom: 12,
    marginBottom: 12,
  } as React.CSSProperties,

  valorBloco: {
    minWidth: 140,
  } as React.CSSProperties,

  valorLabel: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 2,
  } as React.CSSProperties,

  valorNumero: {
    fontWeight: 700,
    fontSize: 14,
  } as React.CSSProperties,

  diagnosticoBox: {
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 12,
    fontSize: 13,
    lineHeight: 1.5,
  } as React.CSSProperties,

  // COR-209: bloco da movimentação do ativo vinculada/relacionada — cor de destaque neutra
  // (azul claro), distinta do diagnóstico (cinza) para chamar atenção sem parecer um erro.
  movimentoAtivoBox: {
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 12,
  } as React.CSSProperties,

  // COR-210: bloco de categoria zerada no período — cor de alerta (âmbar), mesma família visual
  // do AVISO, para reforçar que exige justificativa/análise manual.
  zeramentoBox: {
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 12,
  } as React.CSSProperties,

  fundamentacao: {
    fontSize: 11,
    color: '#6b7280',
    lineHeight: 1.5,
  } as React.CSSProperties,

  // COR-275: bloco de drill-down dos lançamentos relacionados — mesma família visual neutra do
  // diagnóstico, mas com borda própria para se destacar como seção separada.
  lancamentosBox: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 12,
  } as React.CSSProperties,
};

export default AuditoriaDepreciacaoDetalhe;
